import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { apiClient, formatApiError } from "@/lib/api";

const NAVY = "#132a63";
const GREEN = "#8CE313";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
};

const estatusMap = {
  active: { label: "Activa", color: "#5BA628", bg: "#EAF6D6" },
  past_due: { label: "Pago pendiente", color: "#b45309", bg: "#FEF3C7" },
  expired: { label: "Vencida", color: "#E84C5E", bg: "#FEE2E2" },
  cancelled: { label: "Cancelada", color: "#E84C5E", bg: "#FEE2E2" },
};

export default function ConsultaFolio() {
  const { folio: folioParam } = useParams();
  const [folio, setFolio] = useState(folioParam || "");
  const [celular, setCelular] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const consultar = async (e) => {
    e?.preventDefault();
    setError(""); setData(null);
    const cel = celular.replace(/\D/g, "");
    if (!folio.trim() || cel.length < 10) { setError("Ingresa tu folio y celular (10 dígitos)."); return; }
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/funnel/account`, { params: { folio: folio.trim(), celular: cel } });
      setData(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "No pudimos encontrar tu membresía.");
    } finally { setLoading(false); }
  };

  const est = estatusMap[data?.estatus] || { label: data?.estatus || "—", color: NAVY, bg: "#F3F7FF" };

  return (
    <div style={wrap}>
      <Helmet><title>Consulta tu membresía — Mi Ángel Asís</title></Helmet>
      <div style={card} data-testid="consulta-folio-card">
        <div style={{ textAlign: "center", marginBottom: 18 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: NAVY }}>Mi Ángel Asís</div>
          <p style={{ color: "#6b7280", marginTop: 4, fontSize: 14 }}>Consulta el estatus de tu membresía</p>
        </div>

        {!data && (
          <form onSubmit={consultar}>
            <label style={lbl}>Folio</label>
            <input value={folio} onChange={(e) => setFolio(e.target.value)} placeholder="MAA-000001"
              data-testid="consulta-folio-input" style={inp} />
            <label style={lbl}>Celular registrado</label>
            <input value={celular} onChange={(e) => setCelular(e.target.value)} placeholder="55 1234 5678"
              inputMode="numeric" data-testid="consulta-celular-input" style={inp} />
            {error && <div data-testid="consulta-error" style={{ color: "#E84C5E", fontSize: 13, marginTop: 8 }}>{error}</div>}
            <button type="submit" disabled={loading} data-testid="consulta-submit"
              style={{ ...btn, background: NAVY, color: "white", marginTop: 16, opacity: loading ? 0.7 : 1 }}>
              {loading ? "Consultando…" : "Consultar mi membresía"}
            </button>
          </form>
        )}

        {data && (
          <div data-testid="consulta-resultado">
            <div style={{ textAlign: "center", marginBottom: 8 }}>
              <span style={{ background: est.bg, color: est.color, fontWeight: 800, fontSize: 13, padding: "6px 14px", borderRadius: 999 }} data-testid="consulta-estatus">
                Membresía {est.label}
              </span>
            </div>
            <div style={{ background: "#F3F7FF", borderRadius: 14, padding: 18, margin: "16px 0" }}>
              <Row label="Titular" value={data.nombre} />
              <Row label="Folio" value={data.folio} strong testid="consulta-r-folio" />
              <Row label="Inicio" value={fmtDate(data.fecha_inicio)} />
              <Row label="Vigencia hasta" value={fmtDate(data.fecha_vencimiento)} />
            </div>
            {data.beneficiarios?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 700, color: NAVY, marginBottom: 6, fontSize: 14 }}>Beneficiarios</div>
                {data.beneficiarios.map((b, i) => (
                  <div key={i} style={{ fontSize: 14, color: "#374151" }}>
                    <i className="fas fa-user" style={{ color: GREEN, marginRight: 8 }} />{b.nombre} — {b.parentesco || "—"}
                  </div>
                ))}
              </div>
            )}
            <a href="https://wa.me/525500000000" target="_blank" rel="noreferrer" data-testid="consulta-whatsapp"
              style={{ ...btn, background: "#25D366", color: "white", textDecoration: "none", display: "block", textAlign: "center", marginBottom: 10 }}>
              <i className="fab fa-whatsapp" /> Contactar asistencia
            </a>
            <button onClick={() => setData(null)} data-testid="consulta-otra"
              style={{ ...btn, background: "#eef2f7", color: NAVY }}>Consultar otro folio</button>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: 16 }}>
          <Link to="/cuenta/login" style={{ color: NAVY, fontSize: 13, fontWeight: 600 }}>¿Tienes cuenta? Inicia sesión</Link>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, strong, testid }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
      <span style={{ color: "#6b7280", fontSize: 14 }}>{label}</span>
      <span data-testid={testid} style={{ fontWeight: strong ? 800 : 600, color: NAVY, fontSize: strong ? 18 : 14 }}>{value || "—"}</span>
    </div>
  );
}

const wrap = { minHeight: "100vh", background: NAVY, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'Poppins',system-ui,sans-serif" };
const card = { background: "white", color: "#1f2937", borderRadius: 22, padding: "30px 26px", maxWidth: 440, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" };
const lbl = { display: "block", fontSize: 12, fontWeight: 700, color: NAVY, marginTop: 12, marginBottom: 4 };
const inp = { width: "100%", padding: "11px 13px", borderRadius: 10, border: "1px solid #cbd5e1", fontSize: 15, fontFamily: "inherit", boxSizing: "border-box" };
const btn = { width: "100%", padding: "13px", borderRadius: 999, fontWeight: 800, border: "none", cursor: "pointer", fontSize: 15, fontFamily: "inherit" };
